import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

const TIER_LABEL = { free: "Free", basic: "Basic", plus: "Plus", pro: "Pro" };
const TIER_DAILY = { free: 10, basic: 30, plus: 60, pro: 100 };

// Returns the tier that hit its daily AI limit, or null. Handles the current
// `DAILY_LIMIT:<tier>` format and the legacy DAILY_LIMIT_FREE/PREMIUM strings.
export function dailyLimitTier(detail) {
  if (typeof detail !== "string") return null;
  if (detail.startsWith("DAILY_LIMIT:")) {
    const tier = detail.split(":")[1];
    return TIER_LABEL[tier] ? tier : "free";
  }
  if (detail === "DAILY_LIMIT_FREE") return "free";
  if (detail === "DAILY_LIMIT_PREMIUM") return "basic";
  return null;
}

export function isDailyLimit(detail) {
  return !!dailyLimitTier(detail);
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") {
    const tier = dailyLimitTier(detail);
    if (tier) {
      const label = TIER_LABEL[tier] || "Free";
      return `You've reached your ${label} daily AI limit (${TIER_DAILY[tier]}/day). It resets tomorrow.`;
    }
    return detail;
  }
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Global toast helper: routes daily-limit errors to the upgrade modal (via a
// window event) and toasts everything else. Prevents a confusing double message.
export function notifyError(err, t) {
  const detail = err?.response?.data?.detail;
  if (dailyLimitTier(detail)) return; // modal handles it
  const msg = formatApiError(detail);
  toast.error(msg || (t ? t("error_generic") : "Something went wrong. Please try again."));
}

// Classify an API/axios error into a UX-actionable kind so callers can show the
// right message + action (upgrade / retry / choose another file) instead of a
// single generic "try again".
export function classifyError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  const tier = dailyLimitTier(detail);
  if (tier) return { kind: "limit", tier };
  if (status === 503 || detail === "RATE_LIMIT") return { kind: "rate_limit" };
  if (status === 413) return { kind: "too_large" };
  if (status === 400) return { kind: "unsupported", detail: typeof detail === "string" ? detail : null };
  return { kind: "temporary" };
}

// Fire the upgrade modal for a given tier that just hit its daily limit.
export function openLimitModal(tier) {
  window.dispatchEvent(new CustomEvent("evermin:daily-limit", { detail: { tier: tier || "free" } }));
}

// Any 429 daily-limit response opens the upgrade modal automatically.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const tier = dailyLimitTier(err?.response?.data?.detail);
    if (tier) {
      err.isDailyLimit = true;
      err.limitTier = tier;
      openLimitModal(tier);
    }
    return Promise.reject(err);
  }
);

export default api;
